<script setup>
// §8.3 / Addendum §2 Button — six variants. ONE interactive hue (D3/D4) for
// everyday actions; variants differ by fill, border and weight, never by a
// second colour — EXCEPT the two lifecycle exceptions below, which
// deliberately reuse a status colour as their fill so the action reads as
// the opposite of the state it's reversing.
//   primary     = --interactive fill
//   secondary   = --surface + 1px --line
//   tertiary    = transparent + 1px DASHED --interactive — the *additive*
//                 action ("Add row"). Dashed on purpose: differentiated from
//                 primary by shape, not a second colour. Always a leading plus.
//   quiet       = transparent, --text-2 label (low-emphasis neutral)
//   destructive = --status-rejected fill (reject, deactivate) — EXCEPTION
//   destructive-outline = --surface + --status-rejected border/text — a lower-emphasis
//                 destructive action (e.g. "Deactivate" sitting alongside "Save" in a
//                 modal footer, where a solid red fill would out-compete the primary action)
//   success     = --status-approved fill (reactivate) — EXCEPTION, mirrors destructive
// Voice (§9): a button says what happens. "Add time", not "Submit".
import { computed, useSlots } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  variant: { type: String, default: 'primary' }, // primary | secondary | tertiary | quiet | destructive | destructive-outline | success
  size: { type: String, default: 'md' }, // sm | md | lg
  type: { type: String, default: 'button' },
  disabled: { type: Boolean, default: false },
})
const slots = useSlots()

const cls = computed(() => ['l-btn', `is-${props.variant}`, `is-${props.size}`])
// tertiary always carries a leading plus (Addendum §2.1); auto-inject it if the
// caller didn't pass their own icon.
const showAutoPlus = computed(() => props.variant === 'tertiary' && !slots.icon)
</script>

<template>
  <button :class="cls" :type="type" :disabled="disabled">
    <LIcon v-if="showAutoPlus" name="plus" :size="13" />
    <slot name="icon" />
    <slot />
  </button>
</template>

<style scoped>
.l-btn {
  position: relative; /* for the expanded hit area */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  font-family: var(--font-ui);
  font-weight: 600;
  border-radius: var(--r);
  border: 1px solid transparent;
  cursor: pointer;
  transition: background var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease),
    color var(--t-fast) var(--ease);
  white-space: nowrap;
}
/* WCAG 2.2 §2.5.8 — guarantee a ≥24×24 POINTER target on every button,
   including small / icon-only ones, via a transparent expanded hit area rather
   than a larger visible box. Harmless on bigger buttons (it matches their box). */
.l-btn::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  min-width: var(--target-min);
  min-height: var(--target-min);
}
.l-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* sizes (Addendum §2.2) */
.is-sm {
  min-height: 28px;
  padding: 6px 12px;
  font-size: var(--text-chip); /* 11.5 */
  border-radius: var(--r-sm);
}
.is-md {
  min-height: 36px;
  padding: 9px 16px;
  font-size: var(--text-sm); /* 12.5 */
  border-radius: var(--r);
}
.is-lg {
  min-height: 44px;
  padding: 12px 20px;
  font-size: var(--text-nav); /* 13.5 */
  border-radius: var(--r);
}

/* primary — the interactive colour */
.is-primary {
  background: var(--interactive);
  color: var(--interactive-on);
}
.is-primary:hover:not(:disabled) {
  background: var(--interactive-hover);
}

/* secondary — surface + line */
.is-secondary {
  background: var(--surface);
  border-color: var(--line);
  color: var(--text-2);
}
.is-secondary:hover:not(:disabled) {
  background: var(--surface-sunken);
  color: var(--text);
}

/* tertiary — the additive action. Dashed interactive border, never filled. */
.is-tertiary {
  background: transparent;
  border: 1px dashed var(--interactive);
  color: var(--interactive-text);
}
.is-tertiary:hover:not(:disabled) {
  background: var(--interactive-tint);
}

/* quiet — transparent, low-emphasis neutral label */
.is-quiet {
  background: transparent;
  color: var(--text-2);
}
.is-quiet:hover:not(:disabled) {
  background: var(--surface-sunken);
  color: var(--text);
}

/* destructive — danger fill */
.is-destructive {
  background: var(--status-rejected-text);
  color: var(--on-emphasis);
}
.is-destructive:hover:not(:disabled) {
  filter: brightness(0.92);
}

/* destructive-outline — same shape as secondary, danger colour instead of neutral.
   Lower-emphasis than destructive: a red outline reads as "this action is dangerous"
   without competing with a solid-fill primary action in the same footer. */
.is-destructive-outline {
  background: var(--surface);
  border-color: var(--status-rejected-text);
  color: var(--status-rejected-text);
}
.is-destructive-outline:hover:not(:disabled) {
  background: var(--status-rejected-fill);
}

/* success — reversible-restore fill (e.g. Reactivate). Mirrors destructive's
   pattern of reusing a status colour as the button fill. */
.is-success {
  background: var(--status-approved-text);
  color: var(--on-emphasis);
}
.is-success:hover:not(:disabled) {
  filter: brightness(0.92);
}

.l-btn :deep(i) {
  font-size: 14px; /* fallback; each icon also passes an explicit :size */
  flex: none;
}
</style>
